import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../lib/api";
import { useI18n } from "../../i18n";
import { Clock, CheckCircle2, XCircle, Ban, Store, ExternalLink } from "lucide-react";

const STATUSES = [
  { key: "", label_en: "All", label_nl: "Alle" },
  { key: "pending", label_en: "Pending", label_nl: "In behandeling" },
  { key: "approved", label_en: "Approved", label_nl: "Goedgekeurd" },
  { key: "rejected", label_en: "Rejected", label_nl: "Afgewezen" },
  { key: "suspended", label_en: "Suspended", label_nl: "Geschorst" },
];

const TONE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  suspended: "bg-zinc-200 text-zinc-700",
};
const ICONS = { pending: Clock, approved: CheckCircle2, rejected: XCircle, suspended: Ban };

const AdminSellers = () => {
  const { lang } = useI18n();
  const [filter, setFilter] = useState("");
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    apiClient.get(`/admin/sellers${filter ? `?status=${filter}` : ""}`)
      .then((r) => setSellers(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const counts = STATUSES.reduce((acc, st) => {
    acc[st.key] = sellers.filter((s) => !st.key || s.status === st.key).length;
    return acc;
  }, {});

  return (
    <div data-testid="admin-sellers-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Verkopers" : "Sellers"}</h1>
          <div className="font-label text-ink-muted mt-1">{lang === "nl" ? "Marketplace verkopersbeheer" : "Marketplace seller management"}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="seller-status-filters">
        {STATUSES.map((s) => (
          <button
            key={s.key || "all"}
            onClick={() => setFilter(s.key)}
            data-testid={`filter-${s.key || "all"}`}
            className={`px-3 py-1.5 text-xs font-label border ${filter === s.key ? "bg-ink text-white border-ink" : "bg-white border-black/15 hover:border-black/30"}`}
          >
            {lang === "nl" ? s.label_nl : s.label_en}
            {filter === s.key && counts[s.key] != null && <span className="ml-2 opacity-80">{counts[s.key]}</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="text-ink-muted">Loading…</div> : (
        <div className="border border-black/10 bg-white divide-y divide-black/5">
          {sellers.length === 0 && <div className="p-8 text-center text-ink-muted text-sm">{lang === "nl" ? "Geen verkopers." : "No sellers."}</div>}
          {sellers.map((s) => {
            const Icon = ICONS[s.status] || Clock;
            return (
              <Link key={s.seller_id} to={`/admin/sellers/${s.seller_id}`} className="p-4 flex items-center gap-4 hover:bg-bone transition-colors" data-testid={`seller-row-${s.seller_id}`}>
                <div className="w-10 h-10 bg-secondary-warm rounded-full flex items-center justify-center shrink-0"><Store className="w-4 h-4 text-terracotta" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">{s.company_name}{s.is_official && <span className="ml-2 text-[10px] font-label bg-terracotta text-white px-1.5 py-0.5">OFFICIAL</span>}</div>
                  <div className="text-xs text-ink-muted">KvK {s.kvk_number || "—"} · {s.contact_email} · {(s.submitted_at || "").slice(0, 10)}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-label ${TONE[s.status] || ""}`}><Icon className="w-3 h-3" /> {s.status.toUpperCase()}</span>
                <ExternalLink className="w-3.5 h-3.5 text-ink-muted" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSellers;
